import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import useGridCustomCell, { INamedItem } from '@components-clean/core/grid/custom-cell';
import Grid from '@components-clean/core/grid-fe';
import { DateTimeCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { CM2Dataset } from '@interfaces/central-monitoring';
import {
    ConvertedControlsHubPanelObservationReviewLogs,
    ConvertedControlsHubPanelObservations,
    statusRAGChips,
} from '@pages-clean/risk-assessment/risk-log/sections/childArea/cm-controls/helper';
import { PersistentStorageKeys } from '@utils-clean/persistent-storage-keys';
import {
    AuditApplicationArea,
    AuditDataType,
    AuditServiceType,
    NamedItemType,
    PanelType,
} from 'types/graphql-global-types';
import { useLocalState } from '@hooks/index';
import {
    convertYesNoToBoolean,
    stringDateWithoutTimeFromCurrentUtcDate,
    stringDateWithoutTimeFromUtc,
} from '@utils/convertor';
import { GET_NAMED_ITEMS_BY_TYPE } from '@queries/common';
import { cm2Panel_OpraQueries } from '@queries/generated/central-monitoring.generated';
import { getNamedItemsByType } from '@queries/generated/common.generated';
import ControlsHubApi from '@apis/bff-api/central-monitoring/controls-hub';
import DatasetApi from '@apis/bff-api/central-monitoring/dataset';

type Props = {
    studyRef: string;
    panelData?: cm2Panel_OpraQueries;
    observation: ConvertedControlsHubPanelObservations;
    dataset: CM2Dataset;
};

export const CmControlsReviewLogsGrid = ({ studyRef, panelData, observation, dataset }: Props) => {
    const { t, ready } = useTranslation('ram', { keyPrefix: 'riskLog.popOut.obsActions.reviewLogsGrid' });

    const { data: controlsReviewLogDecisionData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.CONTROLS_REVIEW_LOG_DECISION },
    });

    const displayTypes = [t('displayTypes.cumulative'), t('displayTypes.perMonth'), t('displayTypes.perWeek')];

    const { data } = ControlsHubApi.useGetControlsHubChartObservationReviewLogs(studyRef, observation.id!);
    const { data: datasets } = DatasetApi.useGetActive(studyRef);
    const { ChipCell, ActiveInactiveCell, TooltipCell } = useGridCustomCell();
    const { t: tRiskLog } = useTranslation('ram', { keyPrefix: 'riskLog' });
    const chips = useMemo(() => statusRAGChips(tRiskLog), []);
    const [gridData, setGridData] = React.useState<unknown[]>([]);
    const yValueTitle = useRef<string>('');
    const plotTitle = useRef<string>('');
    const numeratorEventsTitle = useRef<string>('');
    const denominatorValueTitle = useRef<string>('');
    const [datasetList, setDatasetList] = React.useState<string[] | undefined>(undefined);
    const { state: localState } = useLocalState();
    const studyName = localState?.selectedStudy?.studyNumber;

    // eslint-disable-next-line max-len
    const exportFileName = `${studyName}_Dataset ${stringDateWithoutTimeFromUtc(dataset.dataCutDate, 'DD-MMM-YYYY')}_${t('exportFileName')}_${panelData?.cm2Panel?.plotTitle}`;

    useEffect(() => {
        if (datasets) {
            const sortedDatasets = datasets
                .filter((d) => d.dataCutDate != null) // exclude undefined/null
                .sort((a, b) => new Date(b.dataCutDate!).getTime() - new Date(a.dataCutDate!).getTime())
                .map((d) => stringDateWithoutTimeFromUtc(d.dataCutDate!, 'DD-MMM-yyyy')!);

            setDatasetList(sortedDatasets);
        }
    }, [datasets]);

    if (panelData && panelData.cm2Panel) {
        plotTitle.current = panelData.cm2Panel.plotTitle
            ? // eslint-disable-next-line max-len
              `${panelData.cm2Panel.plotTitle} | ${tRiskLog('shared.timePeriod')}: ${stringDateWithoutTimeFromUtc(observation.time_period, 'DD-MMM-yyyy')}`
            : '';

        if (panelData.cm2Panel.panelType === PanelType.EVENT_RATES_OVER_TIME) {
            yValueTitle.current = panelData.cm2Panel.eventRatesOverTimeParameters?.yAxisTitle ?? '';
            numeratorEventsTitle.current = panelData.cm2Panel.eventRatesOverTimeParameters?.numeratorName ?? '';
            denominatorValueTitle.current = panelData.cm2Panel.eventRatesOverTimeParameters?.denominatorName ?? '';
        } else {
            yValueTitle.current = panelData.cm2Panel.proportionsOverTimeParameters?.yAxisTitle ?? '';
            numeratorEventsTitle.current = panelData.cm2Panel.proportionsOverTimeParameters?.numeratorName ?? '';
            denominatorValueTitle.current = panelData.cm2Panel.proportionsOverTimeParameters?.denominatorName ?? '';
        }
    }

    useEffect(() => {
        if (data && controlsReviewLogDecisionData && ready && datasetList) {
            const convertedResult: ConvertedControlsHubPanelObservationReviewLogs[] = data.map((item) => {
                return {
                    ...item,
                    panel: {
                        name: item.panel,
                        active: convertYesNoToBoolean(item.panel_active),
                    },
                    decision: {
                        id: item.decision_id,
                        name: item.decision,
                        active: convertYesNoToBoolean(item.decision_active),
                    },
                    dataset: stringDateWithoutTimeFromUtc(item.dataset, 'DD-MMM-yyyy'),
                    time_period: stringDateWithoutTimeFromUtc(item.time_period),
                    updated_date: stringDateWithoutTimeFromUtc(item.updated_date),
                    created_date: stringDateWithoutTimeFromUtc(item.created_date),
                    y_value: item.y_value ? +item.y_value : null,
                    y_value_change: item.y_value_change ? +item.y_value_change : null,
                    numerator_events: item.numerator_events ? +item.numerator_events : null,
                    denominator_count: item.denominator_count ? +item.denominator_count : null,
                };
            });
            setGridData(convertedResult);
        }
    }, [data, controlsReviewLogDecisionData, ready, datasetList]);

    if (!datasetList) return;

    const columns: GridColumnProps[] = [
        {
            id: 'dataset',
            field: 'dataset',
            title: t('columns.dataset'),
            lockedByDefault: true,
            minWidth: 150,
            lovData: datasetList,
        },
        {
            id: 'chartNameTimePeriod',
            field: 'chartNameTimePeriod',
            sortable: false,
            title: plotTitle.current,
            children: [
                {
                    id: 'chartName',
                    field: 'chartName',
                    title: yValueTitle.current,
                    filter: 'text',
                    sortable: false,
                    minWidth: 200,
                    children: [
                        {
                            id: 'y_value',
                            field: 'y_value',
                            title: t('columns.values'),
                            filter: 'numeric',
                            minWidth: 150,
                        },
                        {
                            id: 'y_value_change',
                            field: 'y_value_change',
                            title: t('columns.change'),
                            filter: 'numeric',
                            minWidth: 100,
                        },
                    ],
                },
                {
                    id: 'numerator_events',
                    field: 'numerator_events',
                    title: numeratorEventsTitle.current,
                    filter: 'numeric',
                    minWidth: 150,
                },
                {
                    id: 'denominator_count',
                    field: 'denominator_count',
                    title: denominatorValueTitle.current,
                    filter: 'numeric',
                    minWidth: 150,
                },
            ],
        },
        {
            id: 'status',
            field: 'color_direction_threshold',
            title: t('columns.status'),
            lovData: chips.filter((c) => c.value !== tRiskLog('shared.none')).map((c) => c.value),
            cells: { data: ChipCell(chips, { width: 95 }) },
            filter: 'text',
            minWidth: 150,
        },
        {
            id: 'outOfTolerance',
            field: 'oot_description',
            title: t('columns.outOfTolerance'),
            filter: 'text',
            minWidth: 150,
            lovData: chips
                .filter((c) => c.value !== tRiskLog('shared.belowMinData') && c.value !== tRiskLog('shared.green'))
                .map((c) => c.value),
            cells: { data: ChipCell(chips, { width: 95 }) },
        },
        {
            id: 'toleranceLimit',
            field: 'out_of_tolerance_value',
            title: t('columns.toleranceLimit'),
            filter: 'numeric',
            minWidth: 150,
        },
        {
            id: 'displayType',
            field: 'cumulative_or_time_period',
            title: t('columns.displayType'),
            filter: 'text',
            minWidth: 150,
            lovData: displayTypes,
        },
        {
            id: 'scaleFactor',
            field: 'scale_by',
            title: t('columns.scaleFactor'),
            filter: 'text',
            width: 120,
        },
        {
            id: 'numActions',
            field: 'actions_count',
            title: t('columns.numActions'),
            filter: 'numeric',
            width: 120,
        },
        {
            id: 'decision',
            field: 'decision',
            title: t('columns.decision'),
            filter: 'text',
            filterKey: 'name',
            minWidth: 150,
            lovData: controlsReviewLogDecisionData?.namedItemsByType
                ? controlsReviewLogDecisionData.namedItemsByType.map((item) => item?.name as string)
                : [],
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'comments',
            field: 'comments',
            title: t('columns.comments'),
            filter: 'text',
            minWidth: 200,
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'updatedDate',
            field: 'updated_date',
            title: t('columns.updatedDate'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            cellName: 'DateCell',
            minWidth: 150,
        },
        {
            id: 'updatedBy',
            field: 'updated_by',
            title: t('columns.updatedBy'),
            filter: 'text',
            minWidth: 150,
        },
        {
            id: 'createdDate',
            field: 'created_date',
            title: t('columns.createdDate'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            cellName: 'DateCell',
            minWidth: 150,
            showByDefault: false,
        },
        {
            id: 'createdBy',
            field: 'created_by',
            title: t('columns.createdBy'),
            filter: 'text',
            minWidth: 150,
            showByDefault: false,
        },
        {
            id: 'id',
            field: 'record_id',
            title: t('columns.id'),
            filter: 'text',
            minWidth: 100,
            showByDefault: false,
        },
    ];

    return (
        <Grid
            excludeHiddenRowMenuActions
            title={t('title')}
            maxHeight="100%"
            testDataId="ram-cm-controls-review-logs-grid"
            data={gridData}
            columns={columns}
            filterable
            showAddIcon={false}
            useLoadingOverlay
            scrollable="virtual"
            dataItemKey="id"
            exportFilename={`${exportFileName}_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-YYYY')}`}
            service={AuditServiceType.CORE}
            area={AuditApplicationArea.RISK_ASSESSMENT}
            dataType={AuditDataType.EXCEL_EXPORT}
            persistentStorageKey={String(PersistentStorageKeys['cm-controls-popout-review-logs'])}
        />
    );
};
